/**
 * @file Expert dashboard UI — pages/Dashboard/Expert/Submissions/components/SubmissionsTable.tsx.
 */

import React, { useEffect, useMemo, useState } from "react";
import { SubmissionRow } from "../hooks/useExpertSubmissions";
import "../../AllDataset/ExpertAllDataset.css";

interface SubmissionsTableProps {
  rows: SubmissionRow[];
  onOpenReview: (submissionId: string) => void;
  emptyMessage?: string;
}

const PAGE_SIZE = 50;

const fmtNum = (v: number | null | undefined): string =>
  v != null ? v.toFixed(3) : "-";

const SubmissionsTable: React.FC<SubmissionsTableProps> = ({
  rows,
  onOpenReview,
  emptyMessage = "No datasets match your search. Try a different submission code, contributor name, or email.",
}) => {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [rows]);

  const totalItems = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, page]);

  return (
    <div className="expert-review-panel all-dataset-table-card">
      <h2 className="all-dataset-table-title">Table of datasets requiring review</h2>
      {rows.length === 0 ? (
        <p className="all-dataset-search-empty" role="status">
          {emptyMessage}
        </p>
      ) : (
        <div className="all-dataset-table-wrapper">
          <table className="all-dataset-table">
            <thead>
              <tr>
                <th>Submission code</th>
                <th>Material Sub-type</th>
                <th>Embodied Carbon</th>
                <th>Embodied Carbon Unit</th>
                <th>Data Review</th>
                <th>Data Completeness</th>
                <th>Data Acquisition</th>
                <th>Temporal Representativeness</th>
                <th>Geographical Representativeness</th>
                <th>Technological Representativeness</th>
                <th>Manufacturer Representativeness</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((r) => (
                <tr key={r.submissionId}>
                  <td>{r.submissionCode}</td>
                  <td>{r.subMaterial || "-"}</td>
                  <td>{fmtNum(r.embodiedCarbon)}</td>
                  <td>{r.embodiedCarbonUnit || "-"}</td>
                  <td>{r.dataReview || "-"}</td>
                  <td>{r.dataCompleteness || "-"}</td>
                  <td>{r.dataAcquisition || "-"}</td>
                  <td>{r.temporal || "-"}</td>
                  <td>{r.geographical || "-"}</td>
                  <td>{r.technological || "-"}</td>
                  <td>{r.manufacturer || "-"}</td>
                  <td className="action-cell">
                    <button
                      className="review-btn"
                      type="button"
                      onClick={() => onOpenReview(r.submissionId)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <div className="data-table-pagination" aria-label="Dataset pagination">
          <div className="pagination-info">
            <span>
              Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
              {Math.min(page * PAGE_SIZE, totalItems)} of {totalItems} entries
            </span>
          </div>
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Previous
              </button>
              <div className="pagination-numbers">
                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (page <= 4) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = page - 3 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`pagination-number ${page === pageNum ? "active" : ""}`}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                className="pagination-btn"
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** React component — Submissions Table. */
export default SubmissionsTable;
